/**
 * Il catalogo degli apparecchi riconoscibili in realtà aumentata.
 *
 * ⚠️ **Zero conoscenza del settore in questi tipi.** Il sistema vede APPARECCHI con
 * delle SUPERFICI (fotografie) e dei SEGNI (punti in coordinate dell'immagine): non sa
 * cosa sia un inverter, né cosa sia un connettore. Il giorno in cui si mappa un quadro
 * elettrico, una pompa di calore o una centralina d'allarme, non si tocca una riga —
 * si aggiunge un documento.
 *
 * ⚠️ **Questo contratto è condiviso con l'app Android**, che legge gli stessi documenti
 * e li tiene in copia locale per lavorare senza rete (in un vano tecnico il campo non
 * c'è, ed è lì che serve). Un campo rinominato qui rompe silenziosamente l'app: si
 * aggiunge, non si rinomina, e il vecchio resta finché tutte le app installate non sono
 * aggiornate.
 *
 * ⚠️ snake_case come tutto il resto di Firestore.
 */

/** Una parte dell'apparecchio: un connettore, una spia, un interruttore. */
export interface ParteAr {
  id: string;
  /** Come si chiama in italiano: «Interruttore in continua». */
  nome: string;
  /**
   * La sigla serigrafata dal costruttore: «DC SWITCH», «PV1+».
   *
   * ⚠️ È quella che si legge sull'apparecchio, ed è anche quella che il lettore di
   * scritte dell'app usa per proporre gli abbinamenti. Non si traduce: è stampata.
   */
  sigla: string;
  /** Cosa fa, in una frase. Compare sotto l'indicazione. */
  spiega: string;
}

/** Una domanda che l'utente può toccare: «Le stringhe FV in ingresso». */
export interface DomandaAr {
  testo: string;
  /** Gli id delle parti che si accendono. */
  parti: string[];
}

/**
 * Dove sta una faccia rispetto a chi guarda il fronte.
 *
 * ⚠️ **È un dato dichiarato, non deducibile.** Nessun programma può capire da una
 * fotografia che i collegamenti stanno sotto: serve a mandare l'utente nella direzione
 * giusta quando cerca una parte che dall'inquadratura corrente non si vede.
 */
export interface FacciaAr {
  nome: string;
  /** [x, y]: x positivo a destra, y positivo in basso, rispetto al fronte. */
  direzione: [number, number];
}

/** Un punto segnato su una fotografia, in pixel di quella fotografia. */
export interface SegnoAr {
  parte: string;
  x: number;
  y: number;
  r: number;
  /**
   * Come ci è finito: serve a sapere di cosa fidarsi.
   *
   * ⚠️ Un punto proposto da un modello e mai confermato NON è un punto verificato, e
   * mescolarli renderebbe impossibile sapere quali rileggere. Su un apparecchio in
   * corrente continua è una distinzione che vale la pena tenere.
   */
  origine?: 'umano' | 'bozza_ia' | 'serigrafia';
}

/** Una fotografia di riferimento: una faccia dell'apparecchio. */
export interface SuperficieAr {
  id: string;
  nome: string;
  /** L'id della faccia (fronte, sotto, fianco-sx…): raggruppa più inquadrature. */
  faccia: string;
  /** Percorso su Storage. L'app se lo scarica e lo tiene in copia locale. */
  immagine: string;
  /** URL pubblico di lettura, per la console. */
  immagine_url?: string;
  /**
   * Larghezza reale della porzione inquadrata, in metri.
   *
   * ⚠️ Serve ad ARCore per stimare la distanza — Google: «migliora le prestazioni di
   * rilevamento e monitoraggio, soprattutto per le immagini fisiche oltre 75 cm».
   * Sbagliarla non impedisce il riconoscimento: fa disegnare i cerchi della misura
   * sbagliata.
   */
  larghezza_metri: number;
  larghezza_pixel: number;
  altezza_pixel: number;
  segni: SegnoAr[];
}

/**
 * Un documento di consultazione: una tavola del manuale, uno schema, una foto di
 * catalogo con le parti etichettate.
 *
 * ⚠️ **Serve a CAPIRE, non a riconoscere.** Non è una superficie: ARCore non lo userà
 * mai per agganciare l'apparecchio. Va ai modelli come materiale di riferimento perché
 * sappiano che aspetto ha un DC SWITCH su questo apparecchio prima di cercarlo nella
 * fotografia — e le coordinate restano sempre e solo quelle della fotografia.
 */
export interface RiferimentoAr {
  id: string;
  nome: string;
  /** Percorso su Storage. */
  file: string;
  /** `application/pdf` oppure `image/*`. */
  tipo: string;
  /** Cosa contiene, in una riga: entra nel messaggio ai modelli. */
  descrizione: string;
  byte: number;
  /**
   * A quale insieme appartiene: le tre pagine di uno schema, le due viste di una
   * morsettiera.
   *
   * ⚠️ **Documenti caricati insieme sono quasi sempre UNA cosa sola.** Passarli al
   * modello come tre allegati scollegati lo costringe a dedurre il legame — e su tre
   * pagine di uno stesso schema la deduzione sbagliata è che siano tre schemi diversi,
   * cioè tre apparecchi diversi. Il gruppo lo dichiara chi carica, che lo sa.
   */
  gruppo?: string;
  /** La posizione dentro il gruppo: per un manuale è l'ordine delle pagine. */
  ordine?: number;
}

export interface ApparecchioAr {
  id: string;
  marca: string;
  modello: string;
  descrizione: string;
  /**
   * Frammenti da cercare nel codice letto sulla targhetta.
   *
   * ⚠️ **Il codice identifica, l'inquadratura ancora.** Sono due mestieri diversi: la
   * certezza di quale modello sia la dà il codice — su qualunque esemplare, anche al
   * buio; la fotografia serve solo a sapere dove stanno le parti.
   */
  codici_modello: string[];
  /** Dimensioni reali in millimetri. Zero = non dichiarata. */
  larghezza_mm: number;
  altezza_mm: number;
  profondita_mm: number;
  parti: ParteAr[];
  domande: DomandaAr[];
  facce: Record<string, FacciaAr>;
  superfici: SuperficieAr[];
  /** Manuali, schemi, tavole: materiale di consultazione per i modelli e per chi rifinisce. */
  riferimenti?: RiferimentoAr[];
  /** Se l'app deve scaricarlo. Un apparecchio in lavorazione non si pubblica. */
  pubblicato: boolean;
  aggiornato_il?: unknown;
  aggiornato_da?: string;
  /** Da dove è nato: il telefono che l'ha fotografato, o la console. */
  origine?: 'telefono' | 'console';
}

/** Le facce standard proposte. Un apparecchio può dichiararne altre. */
export const FACCE_STANDARD: Record<string, FacciaAr> = {
  fronte: { nome: 'il fronte', direzione: [0, 0] },
  sotto: { nome: 'il lato inferiore', direzione: [0, 1] },
  sopra: { nome: 'il lato superiore', direzione: [0, -1] },
  'fianco-sx': { nome: 'il fianco sinistro', direzione: [-1, 0] },
  'fianco-dx': { nome: 'il fianco destro', direzione: [1, 0] },
  retro: { nome: 'il retro', direzione: [0, 0] },
};

export const COLLEZIONE_APPARECCHI = 'ar_apparecchi';
export const CARTELLA_STORAGE = 'ar_apparecchi';
